import Fuse, { FuseResultMatch, IFuseOptions } from "fuse.js";

export interface HighlightChunk {
    text: string;
    matched: boolean;
}

export interface FuzzyPodcastResult {
    item: Podcast;
    matches: readonly FuseResultMatch[];
    score: number;
}

const podcastFuseOptions: IFuseOptions<Podcast> = {
    keys: [
        { name: "name", weight: 0.7 },
        { name: "author.name", weight: 0.3 },
    ],
    threshold: 0.4,
    distance: 100,
    ignoreLocation: true,
    includeScore: true,
    includeMatches: true,
    minMatchCharLength: 2,
};

export function normalizeSearchQuery(query: string) {
    return query.trim().replace(/\s+/g, " ");
}

export function createPodcastSearchIndex(podcasts: Podcast[]) {
    return new Fuse(dedupePodcasts(podcasts), podcastFuseOptions);
}

export function searchPodcastsFuzzy(query: string, podcasts: Podcast[]) {
    const index = createPodcastSearchIndex(podcasts);
    return searchPodcastIndex(query, index);
}

export function searchPodcastIndex(query: string, index: Fuse<Podcast>) {
    const normalizedQuery = normalizeSearchQuery(query);
    if (normalizedQuery.length < 2) {
        return [];
    }

    return index.search(normalizedQuery).map((result) => ({
        item: result.item,
        matches: result.matches ?? [],
        score: result.score ?? 1,
    }));
}

export function dedupePodcasts(podcasts: Podcast[]) {
    const seen = new Map<number, Podcast>();

    podcasts.forEach((podcast) => {
        if (podcast?.id && !seen.has(podcast.id)) {
            seen.set(podcast.id, podcast);
        }
    });

    return Array.from(seen.values());
}

export function buildHighlightChunks(text: string, query: string, indices?: readonly [number, number][]) {
    if (!text) {
        return [];
    }

    const ranges = indices?.length ? [...indices] : findQueryRanges(text, query);
    if (ranges.length === 0) {
        return [{ text, matched: false }];
    }

    const chunks: HighlightChunk[] = [];
    let cursor = 0;

    ranges
        .sort((a, b) => a[0] - b[0])
        .forEach(([start, end]) => {
            if (start > cursor) {
                chunks.push({ text: text.slice(cursor, start), matched: false });
            }

            chunks.push({ text: text.slice(start, end + 1), matched: true });
            cursor = end + 1;
        });

    if (cursor < text.length) {
        chunks.push({ text: text.slice(cursor), matched: false });
    }

    return chunks.filter((chunk) => chunk.text.length > 0);
}

export function getMatchIndices(matches: readonly FuseResultMatch[], key: string) {
    return matches.find((match) => match.key === key)?.indices ?? [];
}

function findQueryRanges(text: string, query: string): [number, number][] {
    const normalizedQuery = normalizeSearchQuery(query).toLocaleLowerCase();
    if (normalizedQuery.length < 2) {
        return [];
    }

    const haystack = text.toLocaleLowerCase();
    const ranges: [number, number][] = [];
    let fromIndex = 0;

    while (fromIndex < haystack.length) {
        const index = haystack.indexOf(normalizedQuery, fromIndex);
        if (index === -1) {
            break;
        }

        ranges.push([index, index + normalizedQuery.length - 1]);
        fromIndex = index + normalizedQuery.length;
    }

    return ranges;
}
